/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    UpcomingOffersServiceRepositoryImpl,
    UpcomingOffersServiceRepositoryImplV2,
} from '../impls';
import { IUpcomingOffersService, IUpcomingOffersServiceRepository } from '../interfaces';
import { TiVoNetworkRequestModel } from '../tivo';
import {
    TServicesParams,
    TUpcomingOffer,
    TUpcomingOffersList,
    TUpcomingOffersParams,
    TUpcomingOffersV2Params,
} from '../types';
import { ImageUrlGenerator } from '../utils';

export class UpcomingOffersService implements IUpcomingOffersService {
    private oUpcomingOffersServiceRepository: IUpcomingOffersServiceRepository;

    private oUpcomingOffersServiceRepositoryV2: IUpcomingOffersServiceRepository;

    private mImageBaseUrl: string;

    private mChannelBaseUrl: string;

    private static mTotalUpcomingOffer: number = 0;

    private static mTotalUpcomingOfferV2: number = 0;

    private mDefaultOffset: number = 0;

    private mLimit: number = 10;

    // Cached offers to avoid repeated network calls
    private static cachedOffers: Array<TUpcomingOffer> = [];

    private static cachedOffersV2: Array<TUpcomingOffer> = [];

    public constructor(params: TServicesParams) {
        this.mChannelBaseUrl = params.channelBaseUrl || '';
        this.mImageBaseUrl = params?.imageBaseUrl || '';
        this.oUpcomingOffersServiceRepository = new UpcomingOffersServiceRepositoryImpl(params);
        this.oUpcomingOffersServiceRepositoryV2 = new UpcomingOffersServiceRepositoryImplV2(params);
    }

    private async retrieveUpcomingOffersV2(
        upcomingOffersParams: TUpcomingOffersV2Params,
    ): Promise<TUpcomingOffersList> {
        const {
            objectId,
            offset = this.mDefaultOffset,
            limit = this.mLimit,
            minEndTime,
            requestToken,
            screenType,
        } = upcomingOffersParams;

        // This handles minEndTime must be greater than now-1h
        const safeMinEndTime =
            new Date(minEndTime).getTime() > Date.now() - 60 * 60 * 1000
                ? minEndTime
                : new Date().toISOString();
        const safeOffset = Number.isNaN(offset) ? this.mDefaultOffset : offset;

        const missingIndices = this.getMissingIndices(
            safeOffset,
            limit,
            UpcomingOffersService.cachedOffersV2,
        );
        let requiredUpcomingOffers: TUpcomingOffer[] = [];
        let token = requestToken;

        // Fetch and cache the missing offers if any of them is missing else return cache offers
        if (missingIndices.length > 0) {
            const fetchOffset = missingIndices[0];
            const fetched = await this.oUpcomingOffersServiceRepositoryV2.fetchUpcomingOffers({
                objectId,
                offset: fetchOffset,
                limit: missingIndices.length,
                minEndTime: safeMinEndTime,
                screenType,
            });

            const { upcomingOffers, totalCount, responseToken } = fetched;
            token = responseToken;
            UpcomingOffersService.mTotalUpcomingOfferV2 = totalCount;

            requiredUpcomingOffers = upcomingOffers.map((offer) => {
                return this.updateUpcomingOfferwWithProviderImageUrls(offer);
            });
            for (let i = 0; i < requiredUpcomingOffers.length; i += 1) {
                const index = fetchOffset + i;
                UpcomingOffersService.cachedOffersV2[index] = requiredUpcomingOffers[i];
            }
        }
        requiredUpcomingOffers = this.getOffersFromCache(
            safeOffset,
            limit,
            UpcomingOffersService.cachedOffersV2,
            UpcomingOffersService.mTotalUpcomingOfferV2,
        );

        return {
            upcomingOffers: requiredUpcomingOffers,
            responseToken: token as string,
            totalCount: UpcomingOffersService.mTotalUpcomingOfferV2,
        };
    }

    /**
     * @inheritdoc
     */
    public async getUpcomingOffersForCollectionId(
        upcomingOffersParams: TUpcomingOffersParams,
    ): Promise<TUpcomingOffersList> {
        const {
            objectId,
            offset = this.mDefaultOffset,
            limit = this.mLimit,
            minEndTime,
            screenType,
            requestToken,
            networkCallId,
        } = upcomingOffersParams;

        const safeOffset = Number.isNaN(offset) ? this.mDefaultOffset : offset;
        // This handles minEndTime must be greater than now-1h
        const safeMinEndTime =
            new Date(minEndTime).getTime() > Date.now() - 60 * 60 * 1000
                ? minEndTime
                : new Date().toISOString();

        const missingIndices = this.getMissingIndices(
            safeOffset,
            limit,
            UpcomingOffersService.cachedOffers,
        );
        let requiredUpcomingOffers: TUpcomingOffer[] = [];
        let token = requestToken;

        // Fetch and cache the missing offers if any of them is missing else return cache offers
        if (missingIndices.length > 0) {
            const fetchOffset = missingIndices[0];
            const fetched = await this.oUpcomingOffersServiceRepository.fetchUpcomingOffers({
                objectId,
                offset: fetchOffset,
                limit,
                minEndTime: safeMinEndTime,
                screenType,
            });

            const { upcomingOffers, totalCount, responseToken } = fetched;
            token = responseToken;

            requiredUpcomingOffers = upcomingOffers.map((offer) => {
                return this.updateUpcomingOfferwWithProviderImageUrls(offer);
            });
            UpcomingOffersService.mTotalUpcomingOffer = totalCount;
            for (let i = 0; i < requiredUpcomingOffers.length; i += 1) {
                const index = fetchOffset + i;
                UpcomingOffersService.cachedOffers[index] = requiredUpcomingOffers[i];
            }
        }

        requiredUpcomingOffers = this.getOffersFromCache(
            safeOffset,
            limit,
            UpcomingOffersService.cachedOffers,
            UpcomingOffersService.mTotalUpcomingOffer,
        );

        return {
            responseToken: token as string,
            totalCount: UpcomingOffersService.mTotalUpcomingOffer,
            upcomingOffers: requiredUpcomingOffers,
            networkCallId,
        };
    }

    // eslint-disable-next-line class-methods-use-this
    public clearUpcomingOfferCache(): void {
        UpcomingOffersService.mTotalUpcomingOffer = 0;
        UpcomingOffersService.cachedOffers = [];
    }

    /**
     * @inheritdoc
     */
    public getUpcomingOffersV2ForCollectionId(
        upcomingOffersParams: TUpcomingOffersV2Params,
    ): TiVoNetworkRequestModel<any> {
        const oRequestModel = new TiVoNetworkRequestModel(this.retrieveUpcomingOffersV2.bind(this));
        oRequestModel.addRequestParams(upcomingOffersParams);
        return oRequestModel;
    }

    // eslint-disable-next-line class-methods-use-this
    public clearUpcomingOfferV2Cache(): void {
        UpcomingOffersService.mTotalUpcomingOfferV2 = 0;
        UpcomingOffersService.cachedOffersV2 = [];
    }

    private updateUpcomingOfferwWithProviderImageUrls(upcomingOffers: TUpcomingOffer) {
        const updatedUpcomingOffers: TUpcomingOffer = { ...upcomingOffers };
        const urlGenerator = new ImageUrlGenerator();
        const imageUrl = urlGenerator.buildImageUrl(
            this.mImageBaseUrl,
            upcomingOffers.contentId,
            upcomingOffers.contentType === 'movie' ? 'MOVIE' : 'EPISODE',
            504,
            378,
        );
        const channelUrl = upcomingOffers.channel.logoIndex
            ? urlGenerator.buildImageUrlForChannelLogo(
                  this.mChannelBaseUrl,
                  upcomingOffers.channel.logoIndex,
              )
            : '';

        if (updatedUpcomingOffers) {
            updatedUpcomingOffers.imageUrl = imageUrl;
            updatedUpcomingOffers.channelUrl = channelUrl;
        }

        return updatedUpcomingOffers;
    }

    // Helper function to fetch the cached offers
    // eslint-disable-next-line class-methods-use-this
    private getOffersFromCache(
        offset: number,
        limit: number,
        cachedOffers: Array<TUpcomingOffer>,
        totalCount: number,
    ): TUpcomingOffer[] {
        const offers: TUpcomingOffer[] = [];

        for (let i = 0; i < limit && i + offset < totalCount; i += 1) {
            const index = offset + i;
            const offer = cachedOffers[index];
            offers.push(offer);
        }
        return offers;
    }

    // Helper function to fetch the missing indices of the cached offers
    // eslint-disable-next-line class-methods-use-this
    private getMissingIndices(
        offset: number,
        limit: number,
        cachedOffers: Array<TUpcomingOffer>,
    ): number[] {
        const missingIndices: number[] = [];

        for (let i = 0; i < limit; i += 1) {
            const index = offset + i;
            if (!cachedOffers[index]) {
                missingIndices.push(index);
            }
        }

        return missingIndices;
    }
}
